import { Users, Heart, Shield, Sparkles, Sprout, HandHeart, Flame } from "lucide-react";

const VALUES = [
  { Icon: Sparkles, name: "Faith", body: "We root everything in the love, hope and grace of Christ." },
  { Icon: Sprout, name: "Restoration", body: "We believe every story can be rewritten — no soul is beyond reach." },
  { Icon: Shield, name: "Integrity", body: "We do what we say, quietly and consistently, especially when unseen." },
  { Icon: Heart, name: "Dignity", body: "Every human being is treated as image-bearing and worthy." },
  { Icon: Users, name: "Community", body: "We do life together. Nobody walks restoration alone." },
  { Icon: HandHeart, name: "Servanthood", body: "We lead by kneeling — greatness is measured by service." },
  { Icon: Flame, name: "Courage", body: "We enter hard rooms, ask hard questions, and stay." },
];

const LEADERSHIP = [
  { name: "Rev. Dana K.", role: "Executive Director & Founder", image: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=600&auto=format&fit=crop" },
  { name: "Marcus J.", role: "Director of Brotherhood", image: "https://images.unsplash.com/photo-1503023345310-bd7c1de61c7d?w=600&auto=format&fit=crop" },
  { name: "Tanya H.", role: "Director of Sisterhood", image: "https://images.unsplash.com/photo-1531123897727-8f129e1688ce?w=600&auto=format&fit=crop" },
  { name: "Jamal R.", role: "Director of Youth", image: "https://images.unsplash.com/photo-1531384441138-2736e62e0919?w=600&auto=format&fit=crop" },
];

const BOARD = [
  "Dr. Alicia Morgan — Chair",
  "Pastor David L. — Vice Chair",
  "Sarah P., CPA — Treasurer",
  "Kenneth O., Esq. — Secretary",
  "Renee W. — Community Seat",
  "Elder Marcus B. — Faith Seat",
  "Lauren F. — Youth Advocate",
];

export default function About() {
  return (
    <div data-testid="about-page">
      {/* HERO */}
      <section className="section">
        <div className="container-editorial grid grid-cols-1 gap-12 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <div className="eyebrow">Our story</div>
            <h1 className="mt-4 font-serif text-5xl leading-[1.05] tracking-tight text-ink md:text-6xl">
              Born from a small kitchen table. <em className="not-italic text-clay">Grown</em> around thousands more.
            </h1>
            <div className="mt-8 space-y-5 text-lg leading-relaxed text-ink-muted">
              <p>
                KEYZ Foundation Inc. began in 2015 when a handful of neighbors started meeting weekly around a folding
                table — pastors, parents, a returning citizen and a school counselor — asking a single question:
                <em className="not-italic text-ink"> what does restoration actually look like around here?</em>
              </p>
              <p>
                Eleven years later, that same question still guides our work. We are a faith-based nonprofit walking
                alongside men, women, youth and families through mentorship, life-skills, retreats, and a community
                that refuses to give up on anyone.
              </p>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="overflow-hidden rounded-3xl border border-clay/15">
              <img
                src="https://images.unsplash.com/photo-1758613171176-ea64579c2dcf?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80"
                alt="Friends gathered outdoors"
                className="h-full w-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* MISSION + VISION */}
      <section className="bg-oat-100">
        <div className="section container-editorial grid grid-cols-1 gap-10 md:grid-cols-2">
          <div className="rounded-3xl border border-clay/15 bg-white p-10">
            <div className="eyebrow">Mission</div>
            <p className="mt-4 font-serif text-2xl leading-snug text-ink md:text-3xl">
              To restore lives, build leaders and strengthen communities through faith-rooted mentorship and radical belonging.
            </p>
          </div>
          <div className="rounded-3xl border border-sage/25 bg-sage p-10 text-oat">
            <div className="text-xs font-bold uppercase tracking-[0.22em] text-oat/80">Vision</div>
            <p className="mt-4 font-serif text-2xl leading-snug md:text-3xl">
              A community where every soul knows they carry a key — and never has to unlock their story alone.
            </p>
          </div>
        </div>
      </section>

      {/* CORE VALUES */}
      <section className="section">
        <div className="container-editorial">
          <div className="eyebrow">Seven core values</div>
          <h2 className="mt-3 font-serif text-4xl leading-tight text-ink md:text-5xl">The compass we return to.</h2>
          <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
            {VALUES.map(({ Icon, name, body }, i) => (
              <div key={name} data-testid={`value-${name.toLowerCase()}`} className={`rounded-2xl border border-clay/10 bg-white p-6 ${i === 0 ? "lg:col-span-2" : ""}`}>
                <Icon className="h-6 w-6 text-clay" />
                <div className="mt-4 font-serif text-2xl text-ink">{name}</div>
                <p className="mt-2 text-sm leading-relaxed text-ink-muted">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LEADERSHIP */}
      <section className="bg-oat-100">
        <div className="section container-editorial">
          <div className="eyebrow">Leadership team</div>
          <h2 className="mt-3 font-serif text-4xl leading-tight text-ink md:text-5xl">The people carrying the keys.</h2>
          <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {LEADERSHIP.map((m) => (
              <div key={m.name} data-testid={`leader-${m.name.split(" ").join("-").toLowerCase()}`} className="overflow-hidden rounded-2xl border border-clay/10 bg-white">
                <div className="aspect-[4/5] overflow-hidden">
                  <img src={m.image} alt={m.name} className="h-full w-full object-cover" />
                </div>
                <div className="p-5">
                  <div className="font-serif text-xl text-ink">{m.name}</div>
                  <div className="text-sm text-clay">{m.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BOARD */}
      <section className="section">
        <div className="container-editorial grid grid-cols-1 gap-10 md:grid-cols-12">
          <div className="md:col-span-5">
            <div className="eyebrow">Board of directors</div>
            <h2 className="mt-3 font-serif text-4xl leading-tight text-ink md:text-5xl">Governance grounded in community.</h2>
            <p className="mt-4 text-ink-muted">
              Our board is a mix of local pastors, professionals, alumni and youth advocates — chosen for their
              wisdom and their willingness to show up.
            </p>
          </div>
          <ul className="md:col-span-7 space-y-3">
            {BOARD.map((b) => (
              <li key={b} className="flex items-center gap-4 rounded-xl border border-clay/10 bg-white px-5 py-4">
                <span className="h-2 w-2 rounded-full bg-clay" />
                <span className="text-ink">{b}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ANNUAL IMPACT */}
      <section className="bg-ink text-oat">
        <div className="section container-editorial">
          <div className="text-xs font-bold uppercase tracking-[0.22em] text-clay-200">Annual impact · 2025</div>
          <h2 className="mt-3 font-serif text-4xl leading-tight md:text-5xl">Restoration, in numbers.</h2>
          <div className="mt-12 grid grid-cols-2 gap-6 md:grid-cols-4">
            {[
              { v: "342", l: "Lives restored" },
              { v: "87", l: "Active mentors" },
              { v: "512", l: "Youth served" },
              { v: "46", l: "Families reunited" },
              { v: "12,480", l: "Volunteer hours" },
              { v: "8,930", l: "Meals served" },
              { v: "128", l: "Programs completed" },
              { v: "34", l: "Scholarships awarded" },
            ].map((s) => (
              <div key={s.l} className="border-l border-oat/15 pl-5">
                <div className="font-serif text-4xl md:text-5xl text-oat">{s.v}</div>
                <div className="mt-2 text-xs font-semibold uppercase tracking-wider text-oat/60">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
